/**
 * Layout maths for the My Workspace editor. Pure functions with no DOM access,
 * so the rules the drag preview follows can be unit tested without a browser.
 *
 * The board is an ordered list. Each card spans one to three columns and the
 * CSS grid flows the list row by row. Every function here works on that list
 * or on rectangles measured from the live grid, in display order.
 */

export const SIZE_SPANS = { third: 1, two_thirds: 2, full: 3 };
export const MAX_HISTORY = 20;

export const spanOf = (size) => SIZE_SPANS[size] || 1;

/** The span a stored size occupies on a grid with `columns` columns. */
export const visibleSpan = (size, columns) => Math.min(spanOf(size), columns);

/**
 * The stored size for a span the user resized to. A span the card already
 * shows keeps its size (on a two-column screen `two_thirds` and `full` look
 * the same), a whole row is `full`, anything else is counted in thirds.
 */
export function sizeForSpan(span, currentSize, columns) {
  if (span === visibleSpan(currentSize, columns)) return currentSize;
  if (span >= columns) return 'full';
  return span === 1 ? 'third' : 'two_thirds';
}

/** Snap a free width to the nearest column span. */
export function snapSpan(width, columnWidth, gap, columns) {
  if (!(columnWidth > 0)) return 1;
  return Math.max(1, Math.min(columns, Math.round((width + gap) / (columnWidth + gap))));
}

export function moveItem(list, from, to) {
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** The dragged widget placed at `index` among the others. */
export function projectOrder(widgets, activeId, index) {
  const active = widgets.find((w) => w.id === activeId);
  if (!active) return widgets;
  const others = widgets.filter((w) => w.id !== activeId);
  others.splice(Math.max(0, Math.min(others.length, index)), 0, active);
  return others;
}

/** Widgets in the order of `ids`; widgets missing from `ids` keep their place at the end. */
export function orderByIds(widgets, ids) {
  const byId = new Map(widgets.map((w) => [w.id, w]));
  const ordered = ids.map((id) => byId.get(id)).filter(Boolean);
  widgets.forEach((w) => { if (!ids.includes(w.id)) ordered.push(w); });
  return ordered;
}

/** Push onto a bounded undo stack; the oldest entry falls off. */
export function pushBounded(stack, entry, max = MAX_HISTORY) {
  const next = [...stack, entry];
  return next.length > max ? next.slice(next.length - max) : next;
}

function nearest(items, distance) {
  let best = null;
  let bestDistance = Infinity;
  items.forEach((item) => {
    const d = distance(item);
    if (d < bestDistance) {
      bestDistance = d;
      best = item;
    }
  });
  return best;
}

/**
 * Where a dragged card lands for a pointer at (x, y).
 *
 * `rects` are the cards in display order as {left, top, right, bottom}; the
 * dragged card's own slot (the placeholder) is at `placeholderIndex`. Returns
 * the dragged card's new index, or null to stay where it is.
 *
 * Below every card the card goes last, above every card it goes first.
 * Otherwise the card nearest the pointer decides: on the placeholder itself
 * the card stays; beside a card, its left half means before it and its right
 * half after it; above or below a card, or over one wider than 80 percent of
 * the grid, the vertical side decides.
 */
export function pickSlot(rects, x, y, placeholderIndex, gridWidth, gap = 20) {
  if (!rects.length) return null;
  const last = rects.length - 1;
  const settle = (target) => (target === placeholderIndex ? null : target);
  if (y > Math.max(...rects.map((r) => r.bottom)) + gap / 2) return settle(last);
  if (y < Math.min(...rects.map((r) => r.top)) - gap / 2) return settle(0);
  const indexed = rects.map((r, i) => ({ ...r, i }));
  const band = indexed.filter((r) => y >= r.top - gap / 2 && y <= r.bottom + gap / 2);
  const cand = band.length
    ? band.find((r) => x >= r.left && x <= r.right)
      || nearest(band, (r) => (x < r.left ? r.left - x : x - r.right))
    : nearest(indexed, (r) => Math.hypot(x - (r.left + r.right) / 2, y - (r.top + r.bottom) / 2));
  if (cand.i === placeholderIndex) return null;
  const inBand = y >= cand.top && y <= cand.bottom;
  const wide = cand.right - cand.left > gridWidth * 0.8;
  const after = wide || !inBand
    ? y > (cand.top + cand.bottom) / 2
    : x > (cand.left + cand.right) / 2;
  const target = cand.i + (after ? 1 : 0) - (placeholderIndex >= 0 && placeholderIndex < cand.i ? 1 : 0);
  return settle(Math.max(0, Math.min(last, target)));
}

/**
 * Keyboard up and down: the index that puts the card at `index` into the row
 * above or below, beside the card whose centre is nearest its own. Past the
 * first row it goes to the start, past the last row to the end.
 */
export function verticalTarget(rects, index, up) {
  const me = rects[index];
  const cx = (me.left + me.right) / 2;
  const others = rects.map((r, i) => ({ ...r, i })).filter((r) => r.i !== index);
  let cands = others.filter((r) => (up ? r.bottom <= me.top + 1 : r.top >= me.bottom - 1));
  if (!cands.length) return up ? 0 : rects.length - 1;
  const edge = up ? Math.max(...cands.map((r) => r.bottom)) : Math.min(...cands.map((r) => r.top));
  cands = cands.filter((r) => Math.abs((up ? r.bottom : r.top) - edge) < 2);
  const best = nearest(cands, (r) => Math.abs((r.left + r.right) / 2 - cx));
  return others.indexOf(best) + (cx > (best.left + best.right) / 2 ? 1 : 0);
}
